import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { Search, Filter, Users, TrendingUp, Mail } from "lucide-react";

const AccountsView = () => {
  const { userId } = useParams();
  const [accounts, setAccounts] = useState([]);
  const [filteredAccounts, setFilteredAccounts] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [selectedAccount, setSelectedAccount] = useState(null);

  useEffect(() => {
    fetchAccounts();
  }, [userId]);

  useEffect(() => {
    filterAccounts();
  }, [accounts, searchQuery, statusFilter]);

  const fetchAccounts = async () => {
    try {
      const response = await axios.get(`${API}/accounts/${userId}`);
      setAccounts(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching accounts:", error);
      toast.error("Failed to load accounts");
      setLoading(false);
    }
  };

  const filterAccounts = () => {
    let filtered = accounts;

    if (searchQuery) {
      filtered = filtered.filter(acc => 
        acc.companyName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        acc.industry.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (statusFilter !== "all") {
      filtered = filtered.filter(acc => acc.status === statusFilter);
    }

    setFilteredAccounts(filtered);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "customer": return "bg-green-500/20 text-green-400 border-green-500/50";
      case "qualified": return "bg-blue-500/20 text-blue-400 border-blue-500/50";
      case "engaged": return "bg-yellow-500/20 text-yellow-400 border-yellow-500/50";
      case "prospect": return "bg-gray-500/20 text-gray-400 border-gray-500/50";
      default: return "bg-gray-500/20 text-gray-400 border-gray-500/50";
    }
  };

  const getEngagementColor = (score) => {
    if (score >= 70) return "text-green-400";
    if (score >= 40) return "text-yellow-400";
    return "text-red-400";
  };

  const handleBulkAction = async (action) => {
    toast.info(`${action} action initiated for selected accounts`);
  };

  const handleAccountClick = (account) => {
    setSelectedAccount(account);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-[#3dd9c5]"></div>
      </div>
    );
  }

  if (selectedAccount) {
    return (
      <div className="space-y-6" data-testid="account-detail">
        <div className="flex items-center justify-between">
          <Button
            onClick={() => setSelectedAccount(null)}
            variant="ghost"
            className="text-[#3dd9c5] hover:bg-[#3dd9c5]/10"
          >
            ← Back to Accounts
          </Button>
        </div>

        <Card className="bg-[#0f1419] border-gray-800/50 p-8">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h2 className="text-3xl font-bold text-white mb-2">{selectedAccount.companyName}</h2>
              <p className="text-gray-400">{selectedAccount.industry}</p>
            </div>
            <Badge className={`${getStatusColor(selectedAccount.status)} border text-sm`}>
              {selectedAccount.status}
            </Badge>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
            <div>
              <p className="text-xs text-gray-500 mb-1">Target Persona</p>
              <p className="text-lg font-semibold text-white">{selectedAccount.targetPersona}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Engagement Score</p>
              <p className={`text-lg font-semibold ${getEngagementColor(selectedAccount.engagementScore)}`}>
                {selectedAccount.engagementScore}/100
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Last Contact</p>
              <p className="text-lg font-semibold text-white">
                {selectedAccount.lastContact ? new Date(selectedAccount.lastContact).toLocaleDateString() : "Never"}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Next Action</p>
              <p className="text-lg font-semibold text-[#3dd9c5]">{selectedAccount.nextAction || "TBD"}</p>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-6">
            <h3 className="text-lg font-semibold text-white mb-4">AI-Generated Insights</h3>
            <div className="bg-[#3dd9c5]/10 border border-[#3dd9c5]/30 rounded-lg p-4">
              <p className="text-sm text-gray-300 leading-relaxed">
                Based on {selectedAccount.companyName}'s profile and engagement history, we recommend focusing on {selectedAccount.targetPersona}. 
                The account shows {selectedAccount.engagementScore >= 50 ? "strong" : "moderate"} engagement signals. 
                Key talking points should emphasize ROI and efficiency gains in their {selectedAccount.industry} operations.
              </p>
            </div>
          </div>

          <div className="flex gap-3 mt-6">
            <Button className="bg-[#e91e8c] hover:bg-[#d01a7d] text-white">
              <Mail className="h-4 w-4 mr-2" />
              Generate Outreach
            </Button>
            <Button variant="outline" className="border-[#3dd9c5]/50 text-[#3dd9c5] hover:bg-[#3dd9c5]/10">
              Update Status
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="accounts-view">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-2">
            <Users className="h-8 w-8 text-[#3dd9c5]" />
            Accounts
          </h1>
          <p className="text-gray-400">Manage and track all customer accounts</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => handleBulkAction('Campaign')}
            variant="outline"
            className="border-[#3dd9c5]/50 text-[#3dd9c5] hover:bg-[#3dd9c5]/10"
          >
            Assign to Campaign
          </Button>
          <Button
            onClick={() => handleBulkAction('Outreach')}
            className="bg-[#e91e8c] hover:bg-[#d01a7d] text-white"
          >
            <Mail className="h-4 w-4 mr-2" />
            Generate Outreach
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search accounts..."
            className="pl-10 bg-[#0f1419] border-gray-800 text-white"
            data-testid="search-accounts"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="bg-[#0f1419] border border-gray-800 text-white rounded-lg px-4 py-2"
          data-testid="status-filter"
        >
          <option value="all">All Status</option>
          <option value="prospect">Prospect</option>
          <option value="engaged">Engaged</option>
          <option value="qualified">Qualified</option>
          <option value="customer">Customer</option>
        </select>
      </div>

      {/* Accounts Table */}
      <Card className="bg-[#0f1419] border-gray-800/50">
        <ScrollArea className="h-[600px]">
          <table className="w-full">
            <thead className="border-b border-gray-800 sticky top-0 bg-[#0f1419]">
              <tr>
                <th className="text-left p-4 text-sm font-semibold text-gray-400">Company</th>
                <th className="text-left p-4 text-sm font-semibold text-gray-400">Industry</th>
                <th className="text-left p-4 text-sm font-semibold text-gray-400">Persona</th>
                <th className="text-left p-4 text-sm font-semibold text-gray-400">Status</th>
                <th className="text-left p-4 text-sm font-semibold text-gray-400">Engagement</th>
                <th className="text-left p-4 text-sm font-semibold text-gray-400">Last Contact</th>
                <th className="text-left p-4 text-sm font-semibold text-gray-400">Next Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredAccounts.map((account, idx) => (
                <tr
                  key={account.accountId}
                  onClick={() => handleAccountClick(account)}
                  className="border-b border-gray-800/50 hover:bg-white/5 cursor-pointer transition-colors"
                  data-testid={`account-row-${idx}`}
                >
                  <td className="p-4">
                    <p className="text-white font-semibold">{account.companyName}</p>
                  </td>
                  <td className="p-4 text-gray-400">{account.industry}</td>
                  <td className="p-4 text-gray-400">{account.targetPersona}</td>
                  <td className="p-4">
                    <Badge className={`${getStatusColor(account.status)} border text-xs`}>
                      {account.status}
                    </Badge>
                  </td>
                  <td className="p-4">
                    <span className={`font-semibold ${getEngagementColor(account.engagementScore)}`}>
                      {account.engagementScore}
                    </span>
                  </td>
                  <td className="p-4 text-gray-400">
                    {account.lastContact ? new Date(account.lastContact).toLocaleDateString() : "Never"}
                  </td>
                  <td className="p-4 text-[#3dd9c5] text-sm">{account.nextAction || "TBD"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredAccounts.length === 0 && (
            <div className="text-center py-12">
              <Users className="h-12 w-12 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-400">No accounts found</p>
            </div>
          )}
        </ScrollArea>
      </Card>
    </div>
  );
};

export default AccountsView;
